<template>
  <BaseDropdown
    v-if="hasAnyFilter"
    ref="dropdownRef"
    class="md:hidden"
    @on-change="handleDropdownChange"
    background-class="bg-surface-light"
  >
    <template #trigger>
      <span
        class="bg-surface-light rounded-full items-center justify-center w-12 h-12 flex transition-colors border border-accent"
      >
        <ZoomFilterIcon v-if="!open" class="h-6 w-6 fill-accent" />
        <CloseIcon v-else class="h-6 w-6 fill-accent" />
        <span class="sr-only">
          {{ $t('globals.search') }}
        </span>
      </span>
    </template>

    <div class="-mx-4 pt-2 max-h-[calc(100dvh-120px)] overflow-y-auto">
      <BaseAccordion
        v-if="!hideCategories"
        :open="openAccordion === 'categories'"
        @toggle="() => setAccordion('categories')"
      >
        <template #trigger>
          <span
            :class="[
              openAccordion && openAccordion !== 'categories'
                ? 'text-text'
                : '',
            ]"
          >
            {{ $t('globals.categories') }}
          </span>
        </template>
        <CategoryFilter @onChange="() => closeDropdownAndAccordion()" />
      </BaseAccordion>
      <BaseAccordion
        v-if="!hasSpecialCategories && !hideVenues"
        :open="openAccordion === 'locations'"
        @toggle="() => setAccordion('locations')"
      >
        <template #trigger>
          <span
            :class="[
              openAccordion && openAccordion !== 'locations' ? 'text-text' : '',
            ]"
          >
            {{ $t('globals.locations') }}
          </span>
        </template>
        <LocationFilter @on-change="() => closeDropdownAndAccordion()" />
      </BaseAccordion>
      <BaseAccordion
        v-if="hasSpecialCategories"
        :open="openAccordion === 'specialCategories'"
        @toggle="() => setAccordion('specialCategories')"
      >
        <template #trigger>
          <span
            :class="[
              openAccordion && openAccordion !== 'specialCategories' ? 'text-text' : '',
            ]"
          >
            {{ specialCategoryLabel }}
          </span>
        </template>
        <SpecialCategoryFilter @on-change="() => closeDropdownAndAccordion()" />
      </BaseAccordion>
      <BaseAccordion
        v-if="!hideCalendar"
        :open="openAccordion === 'date'"
        @toggle="() => setAccordion('date')"
      >
        <template #trigger>
          <span
            :class="[
              openAccordion && openAccordion !== 'date' ? 'text-text' : '',
            ]"
          >
            {{ $t('globals.date') }}
          </span>
        </template>
        <DateFilter @on-change="() => closeDropdownAndAccordion()" />
      </BaseAccordion>
      <BaseAccordion
        v-if="!hideSearch"
        :open="openAccordion === 'search'"
        @toggle="() => setAccordion('search')"
      >
        <template #trigger>
          <span
            :class="[
              openAccordion && openAccordion !== 'search' ? 'text-text' : '',
              openAccordion !== 'search' ? 'mb-2' : '',
            ]"
          >
            {{ $t('globals.search') }}
          </span>
        </template>
        <SearchFilter ref="searchRef" @on-enter="closeDropdownAndAccordion" />
      </BaseAccordion>
    </div>
  </BaseDropdown>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';

  import { useGeneralStore } from '@/stores/general';

  import CategoryFilter from '../filters/CategoryFilter.vue';
  import DateFilter from '../filters/DateFilter.vue';
  import LocationFilter from '../filters/LocationFilter.vue';
  import SearchFilter from '../filters/SearchFilter.vue';
  import SpecialCategoryFilter from '../filters/SpecialCategoryFilter.vue';
  import CloseIcon from '../icons/CloseIcon.vue';
  import ZoomFilterIcon from '../icons/ZoomFilterIcon.vue';
  import BaseAccordion from '../ui/BaseAccordion.vue';
  import BaseDropdown from '../ui/BaseDropdown.vue';

  const generalStore = useGeneralStore();
  const hasSpecialCategories = computed(() => (generalStore.customer()?.special_categories ?? 0) > 0);
  const specialCategoryLabel = computed(() => generalStore.customer()?.special_category_name ?? '');
  const hideCategories = computed(() => generalStore.customer()?.hide_categories ?? false);
  const hideVenues = computed(() => generalStore.customer()?.hide_venues ?? false);
  const hideCalendar = computed(() => generalStore.customer()?.hide_calendar ?? false);
  const hideSearch = computed(() => generalStore.customer()?.hide_search ?? false);
  const isSearchOnly = computed(() =>
    !hideSearch.value &&
    hideCategories.value &&
    hideVenues.value &&
    hideCalendar.value &&
    !hasSpecialCategories.value
  );
  const hasAnyFilter = computed(() =>
    hasSpecialCategories.value ||
    !hideCategories.value ||
    !hideVenues.value ||
    !hideCalendar.value ||
    !hideSearch.value
  );

  const open = ref(false);
  const openAccordion = ref<string | null>(null);

  const dropdownRef = ref<InstanceType<typeof BaseDropdown> | null>(null);
  const searchRef = ref<InstanceType<typeof SearchFilter> | null>(null);

  const handleDropdownChange = (val: boolean) => {
    open.value = val;
    if (!val) openAccordion.value = null;
    else if (isSearchOnly.value) setAccordion('search');
  };

  function closeDropdownAndAccordion() {
    openAccordion.value = null;
    dropdownRef.value?.closeDropdown();
    open.value = false;
  }

  function setAccordion(type: string) {
    openAccordion.value = openAccordion.value === type ? null : type;

    if (openAccordion.value === 'search') {
      if (searchRef.value) {
        searchRef.value.clearInput();
        searchRef.value.focusInput();
      }
    }
  }
</script>
